import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getResources, getCategories, logDownload } from '../api';
import { Worker, Viewer } from '@react-pdf-viewer/core';
import { defaultLayoutPlugin } from '@react-pdf-viewer/default-layout';
import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/default-layout/lib/styles/index.css';

export default function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [resources, setResources] = useState([]);
  const [categories, setCategories] = useState([]);
  const [search, setSearch] = useState('');
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPdf, setSelectedPdf] = useState(null);
  const defaultLayoutPluginInstance = defaultLayoutPlugin();

  useEffect(() => {
    Promise.all([getResources(), getCategories()])
      .then(([resRes, catRes]) => {
        setResources(resRes.data);
        setCategories(catRes.data);
      })
      .finally(() => setLoading(false));
  }, []);

  const fetchResources = async (searchVal, categoryIds) => {
    const params = {};
    if (searchVal) params.search = searchVal;
    if (categoryIds.length > 0) params.category_ids = categoryIds.join(',');
    const res = await getResources(params);
    setResources(res.data);
  };

  const handleSearch = async (e) => {
    const val = e.target.value;
    setSearch(val);
    await fetchResources(val, selectedCategories);
  };

  const handleCategoryToggle = async (catId) => {
    let newSelected;
    if (catId === '') {
      newSelected = [];
    } else {
      if (selectedCategories.includes(catId)) {
        newSelected = selectedCategories.filter(id => id !== catId);
      } else {
        newSelected = [...selectedCategories, catId];
      }
    }
    setSelectedCategories(newSelected);
    await fetchResources(search, newSelected);
  };

  const handleView = async (resource) => {
    await logDownload(resource.id);
    setSelectedPdf(resource);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div style={styles.container}>
      {/* Navbar */}
      <nav style={styles.nav}>
        <div style={styles.navBrand}>
          <span style={styles.navTitle}>Boothby Therapy Services</span>
          <span style={styles.navSubtitle}>Employee Learning Hub</span>
        </div>
        <div style={styles.navRight}>
          <button onClick={() => navigate('/profile')} style={styles.navBtn}>👤 Profile</button>
          <button onClick={handleLogout} style={styles.navBtnOutline}>Sign Out</button>
        </div>
      </nav>

      <div style={styles.content}>
        {/* Welcome */}
        <div style={styles.welcome}>
          <h1 style={styles.welcomeTitle}>Welcome back, {user?.first_name}! 👋</h1>
          <p style={styles.welcomeSubtitle}>
            {user?.position || 'Employee'} · Access your training resources below
          </p>
        </div>

        {/* PDF Viewer Modal */}
        {selectedPdf && (
          <div style={styles.modal}>
            <div style={styles.modalContent}>
              <div style={styles.modalHeader}>
                <h3 style={styles.modalTitle}>{selectedPdf.title}</h3>
                <div style={styles.modalActions}>
                  <a href={selectedPdf.file_url} target="_blank" rel="noreferrer" style={styles.downloadBtn}>
                    ⬇️ Download
                  </a>
                  <button onClick={() => setSelectedPdf(null)} style={styles.closeBtn}>
                    ✕ Close
                  </button>
                </div>
              </div>
              <div style={styles.pdfContainer}>
                <Worker workerUrl="/pdf.worker.min.js">
                  <Viewer
                    fileUrl={selectedPdf.file_url}
                    plugins={[defaultLayoutPluginInstance]}
                  />
                </Worker>
              </div>
            </div>
          </div>
        )}

        {/* Search & Filter */}
        <div style={styles.filterRow}>
          <input
            type="text"
            placeholder="Search resources..."
            value={search}
            onChange={handleSearch}
            style={styles.searchInput}
          />
          <div style={styles.filterLabel}>Filter by category:</div>
          <div style={styles.categoryRow}>
            <button
              onClick={() => handleCategoryToggle('')}
              style={selectedCategories.length === 0 ? styles.catBtnActive : styles.catBtn}
            >
              All
            </button>
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => handleCategoryToggle(cat.id)}
                style={selectedCategories.includes(cat.id) ? styles.catBtnActive : styles.catBtn}
              >
                {cat.name}
                {selectedCategories.includes(cat.id) && ' ✓'}
              </button>
            ))}
          </div>
          {selectedCategories.length > 0 && (
            <div style={styles.activeFilters}>
              <span style={styles.activeFiltersLabel}>Active filters: </span>
              {selectedCategories.map(id => {
                const cat = categories.find(c => c.id === id);
                return cat ? (
                  <span key={id} style={styles.activeFilterTag}>
                    {cat.name}
                    <button onClick={() => handleCategoryToggle(id)} style={styles.removeFilterBtn}>×</button>
                  </span>
                ) : null;
              })}
              <button onClick={() => handleCategoryToggle('')} style={styles.clearFiltersBtn}>
                Clear all
              </button>
            </div>
          )}
        </div>

        {/* Resources */}
        {loading ? (
          <div style={styles.loading}>Loading resources...</div>
        ) : resources.length === 0 ? (
          <div style={styles.empty}>No resources found.</div>
        ) : (
          <div style={styles.grid}>
            {resources.map((resource) => (
              <div key={resource.id} style={styles.card}>
                <div style={styles.cardIcon}>📄</div>
                <div style={styles.cardContent}>
                  <h3 style={styles.cardTitle}>{resource.title}</h3>
                  {resource.description && (
                    <p style={styles.cardDesc}>{resource.description}</p>
                  )}
                  <div style={{display: 'flex', gap: '4px', flexWrap: 'wrap'}}>
                    {resource.resource_categories?.length > 0
                      ? resource.resource_categories.map((rc, i) => {
                          const cat = categories.find(c => c.id === rc.category_id);
                          return cat ? <span key={i} style={styles.cardCategory}>{cat.name}</span> : null;
                        })
                      : resource.categories?.name
                      ? <span style={styles.cardCategory}>{resource.categories.name}</span>
                      : null
                    }
                  </div>
                </div>
                <button onClick={() => handleView(resource)} style={styles.viewBtn}>
                  View
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const styles = {
  container: { minHeight: '100vh', background: '#f8fafc', overflowX: 'hidden' },
  nav: { background: '#243C94', padding: '12px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' },
  navBrand: { display: 'flex', flexDirection: 'column' },
  navTitle: { color: '#ffffff', fontSize: '16px', fontWeight: '700' },
  navSubtitle: { color: '#FFA204', fontSize: '11px', fontWeight: '600', letterSpacing: '1px' },
  navRight: { display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' },
  navBtn: { background: 'rgba(255,255,255,0.15)', color: '#ffffff', border: 'none', borderRadius: '8px', padding: '6px 10px', cursor: 'pointer', fontSize: '12px' },
  navBtnOutline: { background: 'transparent', color: '#ffffff', border: '2px solid rgba(255,255,255,0.5)', borderRadius: '8px', padding: '6px 10px', cursor: 'pointer', fontSize: '12px' },
  content: { maxWidth: '1200px', margin: '0 auto', padding: '24px 16px' },
  welcome: { background: '#ffffff', borderRadius: '12px', padding: '24px', marginBottom: '24px', borderLeft: '5px solid #FFA204', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' },
  welcomeTitle: { color: '#243C94', fontSize: '22px', fontWeight: '700', margin: '0 0 8px 0' },
  welcomeSubtitle: { color: '#6b7280', fontSize: '14px', margin: 0 },
  filterRow: { marginBottom: '24px' },
  filterLabel: { color: '#374151', fontSize: '13px', fontWeight: '600', marginBottom: '8px' },
  searchInput: { width: '100%', padding: '12px 16px', border: '2px solid #e5e7eb', borderRadius: '8px', fontSize: '14px', marginBottom: '16px', boxSizing: 'border-box' },
  categoryRow: { display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '8px' },
  catBtn: { background: '#ffffff', color: '#374151', border: '2px solid #e5e7eb', borderRadius: '20px', padding: '6px 14px', cursor: 'pointer', fontSize: '12px' },
  catBtnActive: { background: '#243C94', color: '#ffffff', border: '2px solid #243C94', borderRadius: '20px', padding: '6px 14px', cursor: 'pointer', fontSize: '12px', fontWeight: '600' },
  activeFilters: { display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginTop: '8px' },
  activeFiltersLabel: { color: '#6b7280', fontSize: '12px' },
  activeFilterTag: { background: '#243C94', color: '#ffffff', padding: '4px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '4px' },
  removeFilterBtn: { background: 'none', border: 'none', color: '#ffffff', cursor: 'pointer', fontSize: '14px', padding: 0, lineHeight: 1 },
  clearFiltersBtn: { background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: '12px', textDecoration: 'underline', padding: 0 },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '16px' },
  card: { background: '#ffffff', borderRadius: '12px', padding: '16px', display: 'flex', alignItems: 'flex-start', gap: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', border: '1px solid #f0f0f0', flexWrap: 'wrap' },
  cardIcon: { fontSize: '28px' },
  cardContent: { flex: 1, minWidth: '150px' },
  cardTitle: { color: '#1f2937', fontSize: '15px', fontWeight: '600', margin: '0 0 6px 0' },
  cardDesc: { color: '#6b7280', fontSize: '13px', margin: '0 0 8px 0' },
  cardCategory: { background: '#eff6ff', color: '#243C94', padding: '4px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '600' },
  viewBtn: { background: '#243C94', color: '#ffffff', border: 'none', borderRadius: '8px', padding: '8px 14px', cursor: 'pointer', fontSize: '13px', fontWeight: '600', whiteSpace: 'nowrap' },
  downloadBtn: { background: '#FFA204', color: '#ffffff', border: 'none', borderRadius: '8px', padding: '8px 14px', cursor: 'pointer', fontSize: '13px', fontWeight: '600', textDecoration: 'none' },
  modal: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.8)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' },
  modalContent: { background: '#ffffff', borderRadius: '12px', width: '100%', maxWidth: '900px', height: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' },
  modalHeader: { padding: '16px 24px', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' },
  modalTitle: { color: '#243C94', fontSize: '16px', fontWeight: '700', margin: 0 },
  modalActions: { display: 'flex', gap: '8px' },
  closeBtn: { background: '#f3f4f6', color: '#374151', border: 'none', borderRadius: '8px', padding: '8px 14px', cursor: 'pointer', fontSize: '13px', fontWeight: '600' },
  pdfContainer: { flex: 1, overflow: 'auto' },
  loading: { textAlign: 'center', padding: '60px', color: '#6b7280' },
  empty: { textAlign: 'center', padding: '60px', color: '#6b7280' },
};